import React from "react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Phase 5.8c — HoursSourceBadge
 *
 * Small coloured tag that explains which source the hours came from. Tag values
 * come straight from `/api/reports/efficiency` -> `hours_source`:
 *
 *   MONTH_DECLARED  → payroll-declared totals (TIME_CLOCK_MONTH)  [legacy]
 *   YEARLY_PUNCHES  → annual punches (TIME_CLOCK_YEARLY)          [legacy]
 *   TE350_PUNCHES   → raw TE350 punches
 *   TE350_DECLARED  → payroll-declared totals from TE350 (Phase 9.5)
 *   MIXED           → different sources for different months in the range
 *   NONE            → employee has no punches recorded
 */
const STYLES = {
  MONTH_DECLARED: {
    label: "MONTH",
    long: "Horas pagadas (payroll) — legacy",
    cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    dot: "bg-emerald-400",
  },
  YEARLY_PUNCHES: {
    label: "YEARLY",
    long: "Punches anuales declarados — legacy",
    cls: "border-amber-500/40 bg-amber-500/10 text-amber-200",
    dot: "bg-amber-400",
  },
  TE350_PUNCHES: {
    label: "TE350",
    long: "Punches crudos del time clock",
    cls: "border-sky-500/40 bg-sky-500/10 text-sky-300",
    dot: "bg-sky-400",
  },
  TE350_DECLARED: {
    label: "TE350·D",
    long: "Horas declaradas (ERP) desde el TE350",
    cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    dot: "bg-emerald-400",
  },
  MIXED: {
    label: "MIX",
    long: "Mezcla de fuentes según el mes",
    cls: "border-orange-500/40 bg-orange-500/10 text-orange-300",
    dot: "bg-orange-400",
  },
  NONE: {
    label: "—",
    long: "Sin horas registradas",
    cls: "border-muted text-muted-foreground bg-transparent",
    dot: "bg-muted-foreground",
  },
};

export default function HoursSourceBadge({ source, detail }) {
  if (!source) return null;
  const s = STYLES[source] || STYLES.NONE;
  const months = detail?.months || {};
  const monthEntries = Object.entries(months);

  const tip = (
    <div className="space-y-1.5 text-xs">
      <div className="font-medium">{s.long}</div>
      {monthEntries.length > 0 && (
        <div className="space-y-0.5">
          {monthEntries.map(([ym, tag]) => {
            const child = STYLES[tag] || STYLES.NONE;
            return (
              <div key={ym} className="flex items-center gap-2">
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${child.dot}`} />
                <span className="font-mono">{ym}</span>
                <span>·</span>
                <span>{child.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            data-testid={`hours-source-badge-${source}`}
            className={`h-4 cursor-help px-1.5 text-[9px] font-semibold uppercase tracking-wide ${s.cls}`}
          >
            {s.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-[260px]">
          {tip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
